import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Loader2, Paperclip, Pencil, Search, Sparkles, Trash2, Upload, X } from 'lucide-react'
import {
  fetchDoctrine, fetchDoctrineContent, searchDoctrine, uploadDoctrine, updateDoctrine, deleteDoctrine,
  downloadDoctrine, DoctrineItem, DoctrineUpdate,
} from '../../lib/api/api'
import { useAuth } from '../../contexts/AuthContext'
import { useAttachments } from '../../contexts/AttachmentContext'
import { formatNumber } from '../../shared/utils/format'

const PAGE_SIZE = 20

type EditForm = {
  title: string
  authors: string
  abstract: string
  subject_areas: string
  keywords: string
}

const toEditForm = (row: DoctrineItem): EditForm => ({
  title: row.title || '',
  authors: row.authors || '',
  abstract: row.abstract || '',
  subject_areas: (row.subject_areas || []).join(', '),
  keywords: (row.keywords || []).join(', '),
})

const splitList = (value: string): string[] => value.split(',').map((v) => v.trim()).filter(Boolean)

export default function DoctrinePage() {
  const { t } = useTranslation()
  const { canManageCorpus } = useAuth()
  const { attachDocument, detachDocument, isDocumentAttached } = useAttachments()

  const [items, setItems] = useState<DoctrineItem[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [textQuery, setTextQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [semanticQuery, setSemanticQuery] = useState('')
  const [semanticResults, setSemanticResults] = useState<Array<Record<string, any>> | null>(null)
  const [isSearching, setIsSearching] = useState(false)

  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [expandedContent, setExpandedContent] = useState<string | null>(null)
  const [isLoadingContent, setIsLoadingContent] = useState(false)

  const [editingRow, setEditingRow] = useState<DoctrineItem | null>(null)
  const [editForm, setEditForm] = useState<EditForm>({ title: '', authors: '', abstract: '', subject_areas: '', keywords: '' })
  const [isSaving, setIsSaving] = useState(false)

  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setError(null)
    fetchDoctrine({ q: textQuery || undefined, limit: PAGE_SIZE, offset })
      .then((res) => {
        if (!active) return
        setItems(res.data.items)
        setTotal(res.data.total)
      })
      .catch(() => { if (active) setError(t('doctrine.errors.loadFailed')) })
      .finally(() => { if (active) setIsLoading(false) })
    return () => { active = false }
  }, [textQuery, offset, reloadToken, t])

  const handleTextQueryChange = (value: string) => {
    setTextQuery(value)
    setOffset(0)
  }

  const runSemanticSearch = async () => {
    if (!semanticQuery.trim()) return
    setIsSearching(true)
    setError(null)
    try {
      const res = await searchDoctrine(semanticQuery)
      setSemanticResults(res.data)
    } catch {
      setError(t('doctrine.errors.loadFailed'))
    } finally {
      setIsSearching(false)
    }
  }

  const clearSemanticSearch = () => {
    setSemanticQuery('')
    setSemanticResults(null)
  }

  const handleAttachToggle = (row: DoctrineItem) => {
    const id = String(row.id)
    if (isDocumentAttached(id, 'doctrine')) {
      detachDocument(id, 'doctrine')
    } else {
      attachDocument({ id, title: row.title, type: 'doctrine', doc_type: 'doctrine', file_name: row.title })
    }
  }

  const handleDownload = async (row: DoctrineItem) => {
    try {
      await downloadDoctrine(row.id)
    } catch {
      setError(t('doctrine.errors.downloadFailed'))
    }
  }

  const handleDelete = async (row: DoctrineItem) => {
    if (!window.confirm(t('common.confirm'))) return
    try {
      await deleteDoctrine(row.id)
      setItems((prev) => prev.filter((r) => r.id !== row.id))
    } catch {
      setError(t('doctrine.errors.deleteFailed'))
    }
  }

  const toggleExpand = async (row: DoctrineItem) => {
    if (expandedId === row.id) {
      setExpandedId(null)
      setExpandedContent(null)
      return
    }
    setExpandedId(row.id)
    setExpandedContent(null)
    setIsLoadingContent(true)
    try {
      const res = await fetchDoctrineContent(row.id)
      setExpandedContent(res.data.content)
    } catch {
      setExpandedContent(null)
    } finally {
      setIsLoadingContent(false)
    }
  }

  const openEdit = (row: DoctrineItem) => {
    setEditingRow(row)
    setEditForm(toEditForm(row))
  }

  const handleSaveEdit = async () => {
    if (!editingRow) return
    setIsSaving(true)
    try {
      const body: DoctrineUpdate = {
        title: editForm.title,
        authors: editForm.authors,
        abstract: editForm.abstract,
        subject_areas: splitList(editForm.subject_areas),
        keywords: splitList(editForm.keywords),
      }
      const response = await updateDoctrine(editingRow.id, body)
      setItems((prev) => prev.map((r) => (r.id === editingRow.id ? response.data : r)))
      setEditingRow(null)
    } catch {
      setError(t('doctrine.errors.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleUpload = async () => {
    if (!uploadFile) return
    setIsUploading(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', uploadFile)
      await uploadDoctrine(form)
      setUploadFile(null)
      setIsUploadOpen(false)
      setOffset(0)
      setReloadToken((v) => v + 1)
    } catch {
      setError(t('doctrine.errors.uploadFailed'))
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold text-gray-900 dark:text-gray-100">{t('doctrine.title')}</h1>
        {canManageCorpus && (
          <button
            type="button"
            onClick={() => setIsUploadOpen((v) => !v)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm"
          >
            <Upload size={16} />
            {t('common.upload')}
          </button>
        )}
      </div>

      {canManageCorpus && isUploadOpen && (
        <div className="p-4 border rounded-lg bg-white dark:bg-gray-800 space-y-3">
          <h2 className="text-sm font-medium">{t('doctrine.upload.title')}</h2>
          <input
            type="file"
            accept=".md"
            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            aria-label={t('doctrine.upload.file')}
          />
          <div>
            <button
              type="button"
              onClick={handleUpload}
              disabled={!uploadFile || isUploading}
              className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
            >
              {t('doctrine.upload.submit')}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={textQuery}
            onChange={(e) => handleTextQueryChange(e.target.value)}
            placeholder={t('doctrine.filters.textPlaceholder')}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={semanticQuery}
          onChange={(e) => setSemanticQuery(e.target.value)}
          placeholder={t('doctrine.filters.textPlaceholder')}
          onKeyDown={(e) => { if (e.key === 'Enter') runSemanticSearch() }}
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
        />
        <button
          type="button"
          onClick={runSemanticSearch}
          disabled={isSearching}
          className="flex items-center gap-1 px-3 py-2 rounded-lg bg-purple-600 text-white text-sm disabled:opacity-50"
        >
          <Sparkles size={16} />
          {t('common.semanticSearch')}
        </button>
        {semanticResults && (
          <button type="button" onClick={clearSemanticSearch} className="text-sm text-gray-500 hover:text-gray-700">
            <X size={16} />
          </button>
        )}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {semanticResults ? (
        <div className="space-y-2">
          <h2 className="text-sm font-medium text-gray-700">{t('doctrine.semanticResults.title')}</h2>
          {semanticResults.length === 0 ? (
            <p className="text-gray-500 text-sm">{t('doctrine.semanticResults.empty')}</p>
          ) : (
            <ul className="space-y-2">
              {semanticResults.map((hit, index) => (
                <li key={hit.id ?? index} className="p-3 border rounded-md bg-white dark:bg-gray-800">
                  <div className="font-medium">{hit.title ?? hit.doc_title}</div>
                  {hit.authors && <div className="text-sm text-gray-500">{hit.authors}</div>}
                  {hit.content && <div className="text-sm text-gray-500 mt-1">{String(hit.content).slice(0, 240)}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-gray-400" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('doctrine.list.noResults')}</p>
          ) : (
            <div className="space-y-2">
              {items.map((row) => {
                const attached = isDocumentAttached(String(row.id), 'doctrine')
                const expanded = expandedId === row.id
                return (
                  <div key={row.id} className="p-4 border rounded-lg bg-white dark:bg-gray-800">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{row.title}</h3>
                        <div className="text-sm text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                          {row.authors && <span>{t('doctrine.list.byAuthors', { authors: row.authors })}</span>}
                          {row.year && <span>{formatNumber(row.year)}</span>}
                        </div>
                        {row.abstract && <p className="text-sm text-gray-600 mt-2">{row.abstract}</p>}
                        <button
                          type="button"
                          onClick={() => toggleExpand(row)}
                          className="text-primary-600 hover:underline text-sm mt-2"
                        >
                          {expanded ? t('doctrine.detail.hideText') : t('doctrine.detail.viewText')}
                        </button>
                        {expanded && (
                          <div className="mt-2 text-sm text-gray-600 border-t pt-2 max-h-64 overflow-y-auto whitespace-pre-wrap">
                            {isLoadingContent ? <Loader2 className="animate-spin" size={16} /> : expandedContent}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleAttachToggle(row)}
                          title={attached ? t('common.detach') : t('common.attach')}
                          aria-label={attached ? t('common.detach') : t('common.attach')}
                          className={`p-2 rounded-full ${attached ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                        >
                          <Paperclip size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownload(row)}
                          title={t('common.download')}
                          aria-label={t('common.download')}
                          className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        >
                          <Download size={18} />
                        </button>
                        {canManageCorpus && (
                          <>
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              title={t('common.edit')}
                              aria-label={t('common.edit')}
                              className="p-2 rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                            >
                              <Pencil size={18} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(row)}
                              title={t('common.delete')}
                              aria-label={t('common.delete')}
                              className="p-2 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="px-3 py-1 border rounded-md disabled:opacity-50"
              >
                {t('common.previous')}
              </button>
              <span className="text-sm text-gray-600">
                {t('common.showingRange', {
                  from: formatNumber(offset + 1),
                  to: formatNumber(Math.min(offset + PAGE_SIZE, total)),
                  total: formatNumber(total),
                })}
              </span>
              <button
                type="button"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
                className="px-3 py-1 border rounded-md disabled:opacity-50"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}

      {editingRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">{t('doctrine.edit.title')}</h3>
              <button type="button" onClick={() => setEditingRow(null)} className="text-gray-500 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                aria-label={t('common.edit')}
                className="w-full border rounded-md px-3 py-1.5"
              />
              <input
                type="text"
                value={editForm.authors}
                onChange={(e) => setEditForm({ ...editForm, authors: e.target.value })}
                className="w-full border rounded-md px-3 py-1.5"
              />
              <textarea
                value={editForm.abstract}
                onChange={(e) => setEditForm({ ...editForm, abstract: e.target.value })}
                rows={4}
                className="w-full border rounded-md px-3 py-1.5"
              />
              <input
                type="text"
                value={editForm.subject_areas}
                onChange={(e) => setEditForm({ ...editForm, subject_areas: e.target.value })}
                className="w-full border rounded-md px-3 py-1.5"
              />
              <input
                type="text"
                value={editForm.keywords}
                onChange={(e) => setEditForm({ ...editForm, keywords: e.target.value })}
                className="w-full border rounded-md px-3 py-1.5"
              />
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button type="button" onClick={() => setEditingRow(null)} className="px-3 py-1.5 rounded-md border text-sm">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={isSaving}
                className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
